import { motion } from "framer-motion";

/**
 * MarketAmbient
 * Adds a set of low-opacity, slowly-animated finance icons (candlesticks,
 * coins, currency symbols, charts) that float in the background — creates
 * a "living market" feel behind sections without stealing focus.
 */

const symbols = [
  { char: "₿", size: 42, color: "hsl(26 82% 52%)" },
  { char: "Ξ", size: 40, color: "hsl(217 91% 60%)" },
  { char: "₹", size: 40, color: "hsl(122 39% 49%)" },
  { char: "$", size: 40, color: "hsl(26 82% 52%)" },
  { char: "€", size: 36, color: "hsl(217 91% 60%)" },
  { char: "¥", size: 36, color: "hsl(122 39% 49%)" },
];

const Candle = ({ up = true, size = 24 }) => {
  const color = up ? "hsl(142 71% 45%)" : "hsl(0 84% 60%)";
  return (
    <svg width={size} height={size * 1.8} viewBox="0 0 24 44" fill="none">
      <line x1="12" y1="2" x2="12" y2="42" stroke={color} strokeWidth="2" />
      <rect x="6" y={up ? 10 : 6} width="12" height={up ? 24 : 28} fill={color} />
    </svg>
  );
};

const ChartLine = ({ up = true, size = 60 }) => {
  const color = up ? "hsl(142 71% 45%)" : "hsl(0 84% 60%)";
  const path = up ? "M2 40 L14 26 L28 32 L44 12 L58 4" : "M2 4 L14 20 L28 14 L44 34 L58 42";
  return (
    <svg width={size} height={size * 0.75} viewBox="0 0 60 44" fill="none">
      <path d={path} stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={up ? 58 : 58} cy={up ? 4 : 42} r="3" fill={color} />
    </svg>
  );
};

const items = [
  { type: "sym", i: 0, top: "8%",  left: "4%",  dur: 14, delay: 0, spin: -8 },
  { type: "sym", i: 1, top: "22%", left: "88%", dur: 16, delay: 1, spin: 10 },
  { type: "sym", i: 2, top: "68%", left: "6%",  dur: 18, delay: 2, spin: -6 },
  { type: "sym", i: 3, top: "78%", left: "82%", dur: 15, delay: 0.5, spin: 8 },
  { type: "sym", i: 4, top: "48%", left: "48%", dur: 20, delay: 1.5, spin: 12 },
  { type: "sym", i: 5, top: "34%", left: "30%", dur: 17, delay: 0.8, spin: -10 },
  { type: "candle", up: true,  top: "16%", left: "72%", dur: 12, delay: 0.4 },
  { type: "candle", up: false, top: "56%", left: "18%", dur: 13, delay: 1.2 },
  { type: "candle", up: true,  top: "84%", left: "60%", dur: 14, delay: 0.6 },
  { type: "chart", up: true,  top: "12%", left: "40%", dur: 22, delay: 0.3 },
  { type: "chart", up: false, top: "62%", left: "68%", dur: 24, delay: 1.6 },
];

const MarketAmbient = ({ opacity = 0.08 }) => {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ opacity }}
    >
      {items.map((it, idx) => {
        const style = {
          position: "absolute",
          top: it.top,
          left: it.left,
        };
        let content = null;
        if (it.type === "sym") {
          const s = symbols[it.i];
          content = (
            <span
              className="font-display font-black select-none"
              style={{ fontSize: s.size, color: s.color, textShadow: `0 0 24px ${s.color}` }}
            >
              {s.char}
            </span>
          );
        } else if (it.type === "candle") {
          content = <Candle up={it.up} size={26} />;
        } else if (it.type === "chart") {
          content = <ChartLine up={it.up} size={80} />;
        }
        return (
          <motion.div
            key={idx}
            style={style}
            initial={{ y: 0, rotate: 0 }}
            animate={{
              y: [0, -18, 0, 12, 0],
              rotate: it.type === "sym" ? [0, it.spin || 0, 0] : [0, 0, 0],
            }}
            transition={{
              duration: it.dur,
              delay: it.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {content}
          </motion.div>
        );
      })}
    </div>
  );
};

export default MarketAmbient;
